
/**
 * Keyboard input handler for the diagnostic inspector.
 *
 * All key handling flows through `onKeyUp`, which is also called by `onKeyDown`
 * and `onKeyPress` with `press = true` so that modifier state is kept current.
 * Actual actions only fire on keyup (press = false/undefined) to avoid
 * repeated triggering while a key is held.
 *
 * Within diagnostic mode, one mutually exclusive command sub-mode may be
 * active at a time.  See `setSubMode` for details.
 */

import * as positionCodecs from '../map/position-codecs';


export default class InspectorInput {

    constructor(inspector) {

        this.inspector = inspector;
        this.core = inspector.core;
    }

    init() {

        document.addEventListener('keyup',    this.onKeyUp.bind(this),    false);
        document.addEventListener('keypress', this.onKeyPress.bind(this), false);
        document.addEventListener('keydown',  this.onKeyDown.bind(this),  false);
    }

    onKeyDown(event) {

        this.altDown   = event.altKey;
        this.ctrlDown  = event.ctrlKey;
        this.shiftDown = event.shiftKey;
        this.onKeyUp(event, true);
    }

    onKeyPress(event) {

        this.onKeyUp(event, true);
    }

    /**
     * Switch to a named command sub-mode, or pass null to return to plain
     * diagnostic mode. Activating a new sub-mode automatically deactivates
     * the current one and clears rendering state owned by that command mode.
     *
     * Command modes and affected rendering state:
     *   'renderFlags' — map.overrides.flagX overrides
     *   'tileBBox'    — debug.drawBBoxes
     *   'radar'       — inspector.drawRadar
     *   'freeze'      — controls persistent map freeze state
     *
     * For 'tileBBox', `debug.drawBBoxes` is set by the caller after this
     * method returns.
     */
    setSubMode(name) {

        const map = this.core.getMap();
        const inspector = this.inspector;
        if (!map) return;
        const debug = map.outerMap.overrides;

        if (this.subMode === 'tileBBox') {

            debug.drawBBoxes = false;
        }
        if (this.subMode === 'radar') {

            inspector.drawRadar = false;
        }
        if (this.subMode === 'freeze') {

            inspector.setFreezeControlsActive(false);
        }
        this.subMode = name;

        if (name === 'radar') inspector.drawRadar = true;
        if (name === 'freeze') inspector.setFreezeControlsActive(true);

        const rfLabel = 'Diagnostics mode > Render flags'
            + ' — f:light n:normal d:diffuse s:specular'
            + ' b:bump a:atm h:shadows k:labels'
            + ' l:lambert p:slope x:aspect';
        const labels = {
            renderFlags: rfLabel,
            tileBBox:    'Diagnostics mode > Tile bounding boxes',
            radar:       'Diagnostics mode > Radar',
            freeze:      'Diagnostics mode > Freeze — F: freeze here/unfreeze'
                + ' C: frustum R: reset view'
        };
        inspector.showNotification(labels[name] || 'Diagnostic mode');
    }

    /**
     * Main key handler, called for keyup, keydown, and keypress events.
     *
     * @param {KeyboardEvent} event  The keyboard event.
     * @param {boolean}       press  True when called from keydown or keypress;
     *     false/undefined for genuine keyup.  Most actions are gated on !press
     *     so they fire exactly once per keypress rather than repeatedly while
     *     the key is held.
     *
     * Sets `hit = true` for any key that was handled.  When hit:
     *   - `map.markDirty()` schedules a render frame (a debug option changed).
     *   - `inspector.preventDefault(event)` suppresses the browser's default
     *     action for that key, preventing interference with the page or browser
     *     UI.
     */
    onKeyUp(event, press) {

        const map = this.core.getMap();
        const inspector = this.inspector;

        if (!map || !event) return;

        const debug = map.outerMap.overrides;

        this.altDown   = event.altKey;
        this.ctrlDown  = event.ctrlKey;
        this.shiftDown = event.shiftKey;

        let hit = false;

        const keyCode = event.keyCode;

        // Shift+D — toggle diagnostic mode (works regardless of current sub-mode)
        if (this.shiftDown && !press && !this.ctrlDown
                && (keyCode === 68 || keyCode === 100)) {

            this.diagnosticMode = !this.diagnosticMode;
            if (this.diagnosticMode) {

                inspector.enableInspector();
                inspector.showNotification('Diagnostic mode');
                // Enabling diagnostic mode does not change rendering state,
                // so hit is not set — markDirty is not needed.
            } else {

                if (this.subMode === 'tileBBox') {

                    debug.drawBBoxes = false;
                }
                if (this.subMode === 'radar') {

                    inspector.drawRadar = false;
                }
                if (this.subMode === 'freeze') {

                    inspector.setFreezeControlsActive(false);
                }
                this.subMode = null;
                inspector.showNotification('Diagnostic mode off');
                hit = true;
            }
        }

        if (this.diagnosticMode) {

            // plain-key sub-mode handlers — keyup only, no modifier
            if (!this.shiftDown && !press) {

                if (keyCode === 27 && this.subMode !== null) { // Escape

                    this.setSubMode(null);
                    hit = true;
                }

                if (this.subMode === 'renderFlags') {

                    switch (keyCode) {

                    case 70: case 102: {  // f — lighting

                        const rfF = !(debug.flagLighting
                            ?? map.config.mapFlagLighting);
                        debug.flagLighting = rfF;
                        inspector.showNotification(
                            'Lighting ' + (rfF ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 78: case 110: {  // n — normal maps

                        const rfN = !(debug.flagNormalMaps
                            ?? map.config.mapFlagNormalMaps);
                        debug.flagNormalMaps = rfN;
                        inspector.showNotification(
                            'Normal maps ' + (rfN ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 68: case 100: {  // d — diffuse maps

                        const rfD = !(debug.flagDiffuseMaps
                            ?? map.config.mapFlagDiffuseMaps);
                        debug.flagDiffuseMaps = rfD;
                        inspector.showNotification(
                            'Diffuse maps ' + (rfD ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 83: case 115: {  // s — specular maps

                        const rfS = !(debug.flagSpecularMaps
                            ?? map.config.mapFlagSpecularMaps);
                        debug.flagSpecularMaps = rfS;
                        inspector.showNotification(
                            'Specular maps ' + (rfS ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 66: case 98: {   // b — bump maps

                        const rfB = !(debug.flagBumpMaps
                            ?? map.config.mapFlagBumpMaps);
                        debug.flagBumpMaps = rfB;
                        inspector.showNotification(
                            'Bump maps ' + (rfB ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 65: case 97: {   // a — atmosphere

                        const rfA = !(debug.flagAtmosphere
                            ?? map.config.mapFlagAtmosphere);
                        debug.flagAtmosphere = rfA;
                        inspector.showNotification(
                            'Atmosphere ' + (rfA ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 72: case 104: {  // h — shadows

                        const rfH = !(debug.flagShadows
                            ?? map.config.mapFlagShadows);
                        debug.flagShadows = rfH;
                        inspector.showNotification(
                            'Shadows ' + (rfH ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 75: case 107: {  // k — labels

                        const rfK = !(debug.flagLabels
                            ?? map.config.mapFlagLabels);
                        debug.flagLabels = rfK;
                        inspector.showNotification(
                            'Labels ' + (rfK ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 76: case 108: {  // l — Lambertian shading

                        const rfL = !(debug.flagShadingLambertian
                            ?? map.config.mapShadingLambertian);
                        debug.flagShadingLambertian = rfL;
                        inspector.showNotification(
                            'Lambertian shading ' + (rfL ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 80: case 112: {  // p — slope shading

                        const rfP = !(debug.flagShadingSlope
                            ?? map.config.mapShadingSlope);
                        debug.flagShadingSlope = rfP;
                        inspector.showNotification(
                            'Slope shading ' + (rfP ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 88: case 120: {  // x — aspect shading

                        const rfX = !(debug.flagShadingAspect
                            ?? map.config.mapShadingAspect);
                        debug.flagShadingAspect = rfX;
                        inspector.showNotification(
                            'Aspect shading ' + (rfX ? 'on' : 'off'));
                        hit = true; break;
                    }
                    case 86: case 118: {  // v — vertical exaggeration

                        const rfV = !(debug.flagVerticalExaggeration
                            ?? map.config.mapFlagVerticalExaggeration);
                        debug.flagVerticalExaggeration = rfV;
                        inspector.showNotification(
                            'Vertical exaggeration ' + (rfV ? 'on' : 'off'));
                        hit = true; break;
                    }
                    }
                }

                if (this.subMode === 'tileBBox') {

                    let bboxHit = true;
                    switch (keyCode) {

                    case 76: case 108:  debug.drawLods = !debug.drawLods; break;  // l
                    case 80: case 112:  debug.drawPositions = !debug.drawPositions; break;  // p
                    case 84: case 116:  debug.drawTextureSize = !debug.drawTextureSize; break;  // t
                    case 70: case 102:  debug.drawFaceCount = !debug.drawFaceCount; break;  // f
                    case 71: case 103:  debug.drawGeodataOnly = !debug.drawGeodataOnly; break;  // g
                    case 68: case 100:  debug.drawDistance = !debug.drawDistance; break;  // d
                    case 78: case 110:  debug.drawNodeInfo = !debug.drawNodeInfo; break;  // n
                    case 77: case 109:  debug.drawMeshBBox = !debug.drawMeshBBox; break;  // m
                    case 73: case 105:  debug.drawIndices = !debug.drawIndices; break;  // i
                    case 82: case 114:  debug.drawResources = !debug.drawResources; break;  // r
                    case 83: case 115:  debug.drawSurfaces = !debug.drawSurfaces; break;  // s
                    case 90: case 122:  debug.drawSurfaces2 = !debug.drawSurfaces2; break;  // z
                    case 67: case 99:   debug.drawCredits = !debug.drawCredits; break;  // c
                    case 69: case 101:  // e
                        debug.debugTextSize = (debug.debugTextSize == 2.0) ? 3.0 : 2.0;
                        break;
                    case 75: case 107:  debug.drawGPixelSize = !debug.drawGPixelSize; break;  // k
                    default: bboxHit = false; break;
                    }
                    if (bboxHit) hit = true;
                }

                if (this.subMode === 'freeze') {

                    if (keyCode === 70 || keyCode === 102) {  // f

                        inspector.toggleFreeze();
                        hit = true;
                    }

                    if (keyCode === 67 || keyCode === 99) {  // c

                        inspector.toggleFreezeFrustum();
                        hit = true;
                    }

                    if (keyCode === 82 || keyCode === 114) {  // r

                        inspector.resetFreezeView();
                        hit = true;
                    }
                }
            }

            // Shift+key diagnostic shortcuts
            if (this.shiftDown && !press) {

                switch (keyCode) {

                case 70: case 102:  // Shift+F — render-flags sub-mode
                    this.setSubMode(
                        this.subMode === 'renderFlags' ? null : 'renderFlags');
                    hit = true; break;

                case 66: case 98:   // Shift+B — tile-bbox sub-mode (bbox view)
                    if (this.subMode === 'tileBBox') {

                        this.setSubMode(null);
                    } else {

                        this.setSubMode('tileBBox');
                        debug.drawBBoxes = true;
                    }
                    hit = true; break;

                case 76: case 108:  // Shift+L — radar sub-mode
                    this.setSubMode(this.subMode === 'radar' ? null : 'radar');
                    hit = true; break;

                case 90: case 122:  // Shift+Z — freeze controls
                    this.setSubMode(
                        this.subMode === 'freeze' ? null : 'freeze');
                    hit = true; break;

                case 67: case 99:   // Shift+C — shake camera
                    inspector.shakeCamera = !inspector.shakeCamera;
                    hit = true; break;

                case 49: {  // Shift+1 — copy position to clipboard

                    const map1 = this.core.getMap();
                    if (map1) {

                        const posStr = positionCodecs.vts.encodeString(
                            map1.outerMap.camera.withAltitudeRef('datum'));
                        navigator.clipboard.writeText('pos=' + posStr)
                            .then(function() {

                                inspector.showNotification(
                                    'Position copied to clipboard');
                            });
                    }
                    hit = true; break;
                }

                case 50: {  // Shift+2 — paste position from clipboard

                    const self = this;
                    navigator.clipboard.readText().then(function(text) {

                        const match = text.match(/pos=([^\s&]+)/);
                        if (match) {

                            const items = match[1].split(',');
                            for (let i = 1; i < items.length; i++) {

                                if (i !== 3) items[i] = parseFloat(items[i]);
                            }
                            if (self.core.getMap()) {

                                self.core.setPosition(items);
                                inspector.showNotification(
                                    'Position applied from clipboard');
                            }
                        } else {

                            inspector.showNotification(
                                'No position found in clipboard');
                        }
                    });
                    hit = true; break;
                }

                case 80: case 112:  // Shift+P — save screenshot
                    map.renderer.saveScreenshot('file', 'vts-screenshot.png', 'png');
                    hit = true; break;

                case 83: case 115:  // Shift+S — stats panel
                    inspector.stats.switchPanel(); hit = true; break;

                case 69: case 101:  // Shift+E — stylesheets panel
                    inspector.stylesheets.switchPanel(); hit = true; break;

                case 65: case 97:   // Shift+A — label boxes
                    debug.drawLabelBoxes = !debug.drawLabelBoxes; hit = true; break;

                case 75: case 107:  // Shift+K — all labels
                    debug.drawAllLabels = !debug.drawAllLabels; hit = true; break;

                case 71: case 103:  // Shift+G — mesh stats
                    debug.meshStats = !debug.meshStats; hit = true; break;

                case 77: case 109:  // Shift+M — suspend tile loader
                    map.loaderSuspended = !map.loaderSuspended; hit = true; break;

                case 74: case 106:  // Shift+J — draw earth
                    debug.drawEarth = !debug.drawEarth; hit = true; break;

                case 82: case 114:  // Shift+R — graphs panel
                    inspector.graphs.switchPanel(); hit = true; break;

                case 79: case 111:  // Shift+O — ortho camera
                    map.outerMap.camera.setProjection({
                        ortho: !map.outerMap.camera.projection.ortho });
                    hit = true; break;

                }
            }
        }

        if (hit) {

            map.markDirty();
            inspector.preventDefault(event);
        }
    }

    /**
     * Apply a debug parameter that was specified as a URL query string entry.
     * Called during page initialisation by the map config loader when it
     * encounters a `debugX=…` parameter.  Mirrors the interactive shortcuts so
     * that a URL can reproduce any debug view without manual key presses.
     *
     * For `debugBBox` the value is a string of capital letters that enable
     * individual bbox annotations (e.g. `"LPN"` → lods + positions + node
     * info). For all other parameters the value is a boolean or number.
     *
     * Also sets `this.subMode` so the corresponding sub-mode key handler is
     * immediately active without requiring the user to press the entry shortcut.
     *
     * @param {string} key    Parameter name (e.g. 'debugBBox', 'debugRadar').
     * @param {string} value  Parameter value from the URL.
     */
    setParameter(key, value) {

        const map = this.core.getMap();
        const inspector = this.inspector;

        if (!map) return;

        const debug = map.outerMap.overrides;
        const getBool = () => (value === true || value == 'true' || value == '1');

        switch (key) {

        case 'debugMode': this.diagnosticMode = true; break;
        case 'debugBBox': {
            debug.drawBBoxes = true;

            const has = (a) => value.indexOf(a) != -1;
            if (has('L')) debug.drawLods = true;
            if (has('P')) debug.drawPositions = true;
            if (has('T')) debug.drawTextureSize = true;
            if (has('F')) debug.drawFaceCount = true;
            if (has('G')) debug.drawGeodataOnly = true;
            if (has('D')) debug.drawDistance = true;
            if (has('N')) debug.drawNodeInfo = true;
            if (has('M')) debug.drawMeshBBox = true;
            if (has('I')) debug.drawIndices = true;
            if (has('S')) debug.drawSurfaces = true;
            if (has('Z')) debug.drawSurfaces2 = true;
            if (has('C')) debug.drawCredits = true;
            if (has('E')) debug.debugTextSize = 3.0;
            if (has('K')) debug.drawGPixelSize = true;
            this.subMode = 'tileBBox';
            break;
        }
        case 'debugLBox':      debug.drawLabelBoxes = getBool(); break;
        case 'debugNoEarth':   debug.drawEarth = !getBool(); break;
        case 'debugGridCells': debug.drawGridCells = getBool(); break;
        case 'debugRadar': {

            inspector.enableInspector();
            inspector.drawRadar = true;
            this.subMode = 'radar';
            break;
        }
        }

        map.markDirty();
    }
}
